import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { Link } from 'react-router-dom';
import { Player } from '@remotion/player';
import { Check, Clapperboard, Coins, Download, Sparkles, Wand2 } from 'lucide-react';
import api from '../../lib/api';
import Button from '../../components/ui/Button';
import Textarea from '../../components/ui/Textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import {
  getPromptStudioDurationInFrames,
  PromptStudioComposition,
  type PromptStudioCompositionProps,
} from '../../remotion/PromptStudioComposition';

type RefineVariant = { id: string; label: string; prompt: string };

const DEFAULT_PREVIEW: PromptStudioCompositionProps = {
  fps: 30,
  scenes: [
    {
      title: 'Describe your reel',
      subtitle: 'We turn your prompt into a short motion story',
      durationInFrames: 90,
      bgFrom: '#1e293b',
      bgTo: '#0f172a',
    },
    {
      title: 'Preview here',
      subtitle: 'Each generation uses AI credits',
      durationInFrames: 75,
      bgFrom: '#4c1d95',
      bgTo: '#312e81',
    },
  ],
};

export default function AgencyAnimationStudio() {
  const [prompt, setPrompt] = useState('');
  const [composition, setComposition] = useState<PromptStudioCompositionProps>(DEFAULT_PREVIEW);
  const [generateCreditCost, setGenerateCreditCost] = useState(5);
  const [exportCreditCost, setExportCreditCost] = useState(3);
  const [refineCreditCost, setRefineCreditCost] = useState(1);
  const [balance, setBalance] = useState<number | null>(null);
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usedFallback, setUsedFallback] = useState(false);
  const [lastRunId, setLastRunId] = useState<string | null>(null);
  const [hasGeneratedComposition, setHasGeneratedComposition] = useState(false);
  const [refining, setRefining] = useState(false);
  const [refineVariants, setRefineVariants] = useState<RefineVariant[] | null>(null);
  const [refineSummary, setRefineSummary] = useState<string | null>(null);
  const [selectedRefineId, setSelectedRefineId] = useState<string | null>(null);
  const [refineRunId, setRefineRunId] = useState<string | null>(null);

  const loadBalance = useCallback(async () => {
    try {
      const { data } = await api.get<{ balance: number }>('/agency/ai-credits/me');
      setBalance(data.balance);
    } catch {
      setBalance(null);
    } finally {
      setLoadingBalance(false);
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get<{
          generateCreditCost?: number;
          exportCreditCost?: number;
          refinePromptCreditCost?: number;
          creditCost?: number;
        }>('/agency/animation-studio/credit-cost');
        if (!active) return;
        if (typeof data.generateCreditCost === 'number') setGenerateCreditCost(data.generateCreditCost);
        else if (typeof data.creditCost === 'number') setGenerateCreditCost(data.creditCost);
        if (typeof data.exportCreditCost === 'number') setExportCreditCost(data.exportCreditCost);
        if (typeof data.refinePromptCreditCost === 'number') setRefineCreditCost(data.refinePromptCreditCost);
      } catch {
        /* keep defaults */
      }
    })();
    void loadBalance();
    return () => {
      active = false;
    };
  }, [loadBalance]);

  const durationInFrames = useMemo(() => getPromptStudioDurationInFrames(composition), [composition]);
  const fps = composition.fps || 30;

  const onRefinePrompt = async () => {
    setError(null);
    setRefineSummary(null);
    setRefineVariants(null);
    setSelectedRefineId(null);
    setRefineRunId(null);
    setRefining(true);
    const id =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      const { data } = await api.post<{
        refineRunId: string;
        summary?: string;
        variants: RefineVariant[];
        usedFallback?: boolean;
        aiError?: string;
        creditsCharged: number;
        balanceAfter: number;
      }>('/agency/animation-studio/refine-prompt', { prompt, refineRunId: id });
      setRefineRunId(data.refineRunId);
      setRefineVariants(data.variants);
      setRefineSummary(data.summary ?? null);
      setBalance(data.balanceAfter);
      if (data.usedFallback && data.aiError) {
        setError(
          `Suggestions used a built-in template (${data.aiError}). ${data.creditsCharged} credit was still charged for this refine.`
        );
      }
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { error?: string; balance?: number } } };
      setError(err.response?.data?.error || 'Could not refine prompt');
      if (typeof err.response?.data?.balance === 'number') {
        setBalance(err.response.data.balance);
      }
    } finally {
      setRefining(false);
    }
  };

  const applyRefineVariant = (v: RefineVariant) => {
    setPrompt(v.prompt);
    setSelectedRefineId(v.id);
    setError(null);
  };

  const onGenerate = async () => {
    setError(null);
    setUsedFallback(false);
    setGenerating(true);
    const runId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    try {
      const { data } = await api.post<{
        composition: PromptStudioCompositionProps;
        runId: string;
        usedFallback?: boolean;
        aiError?: string;
        creditsCharged: number;
        balanceAfter: number;
      }>('/agency/animation-studio/generate', { prompt, runId });
      setComposition(data.composition);
      setBalance(data.balanceAfter);
      setLastRunId(data.runId);
      setHasGeneratedComposition(true);
      setUsedFallback(!!data.usedFallback);
      if (data.usedFallback && data.aiError) {
        setError(`Preview used a built-in layout (${data.aiError}). Credits were still charged for this run.`);
      }
    } catch (e: unknown) {
      const err = e as { response?: { status?: number; data?: { error?: string; balance?: number } } };
      const msg = err.response?.data?.error || 'Could not generate';
      setError(msg);
      if (typeof err.response?.data?.balance === 'number') {
        setBalance(err.response.data.balance);
      }
    } finally {
      setGenerating(false);
    }
  };

  const onExportMp4 = async () => {
    setError(null);
    setExporting(true);
    try {
      const res = await api.post(
        '/agency/animation-studio/export-mp4',
        { composition },
        { responseType: 'blob' }
      );
      const ct = String(res.headers['content-type'] || '');
      if (!ct.includes('video/mp4')) {
        const text = await (res.data as Blob).text();
        try {
          const j = JSON.parse(text) as { error?: string };
          setError(j.error || 'Export failed');
        } catch {
          setError(text.slice(0, 200) || 'Export failed');
        }
        void loadBalance();
        return;
      }
      const balHeader = res.headers['x-animation-studio-balance-after'];
      if (balHeader != null) {
        const n = Number(balHeader);
        if (Number.isFinite(n)) setBalance(n);
      } else {
        void loadBalance();
      }
      const blob = res.data as Blob;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'animation-studio.mp4';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e: unknown) {
      const err = e as {
        response?: { status?: number; data?: Blob | { error?: string }; headers?: Record<string, string> };
      };
      const data = err.response?.data;
      if (data instanceof Blob) {
        try {
          const text = await data.text();
          const j = JSON.parse(text) as { error?: string };
          setError(j.error || 'Export failed');
        } catch {
          setError('Export failed');
        }
      } else {
        const msg = (data as { error?: string } | undefined)?.error || 'Export failed';
        setError(msg);
      }
      void loadBalance();
    } finally {
      setExporting(false);
    }
  };

  const canGenerate = prompt.trim().length >= 3 && !generating;
  const canRefine = prompt.trim().length >= 8 && !refining && !generating;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title flex items-center gap-2">
          <Clapperboard className="w-8 h-8 text-accent2" />
          Animation studio
        </h1>
        <p className="text-glass-text-2 text-sm mt-1 max-w-3xl">
          Type a creative brief and we compose a short multi-scene motion story you can preview in the browser. Each
          successful generation charges{' '}
          <span className="text-glass-text font-medium">{generateCreditCost} AI credits</span>; optional refine adds{' '}
          <span className="text-glass-text font-medium">{refineCreditCost} credit</span> per run (three directions).
          Downloading MP4 uses{' '}
          <span className="text-glass-text font-medium">{exportCreditCost} additional credits</span> per export, from
          the same pool as{' '}
          <Link to="/agency/ai-video-edits" className="text-accent2 hover:underline">
            AI video edits
          </Link>
          .
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="inline-flex items-center gap-1.5 rounded-lg border border-glass bg-glass-2 px-3 py-1.5 text-glass-text">
          <Coins className="w-4 h-4 text-accent2" />
          {loadingBalance ? 'Loading balance…' : `${balance ?? 0} credits available`}
        </span>
        <Link to="/agency/ai-credits" className="text-accent2 hover:underline text-sm">
          View ledger
        </Link>
      </div>

      {error && (
        <p className="text-amber-300/95 text-sm border border-amber-500/30 bg-amber-500/10 rounded-lg px-3 py-2">
          {error}
        </p>
      )}

      {usedFallback && !error && (
        <p className="text-glass-text-2 text-sm">
          AI returned a safe fallback layout; credits were applied for this generation.
        </p>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-5">
          <CardHeader>
            <CardTitle className="text-glass-text flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-accent2" />
              Prompt
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Textarea
              value={prompt}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
                setPrompt(e.target.value);
                setRefineVariants(null);
                setRefineSummary(null);
                setSelectedRefineId(null);
                setRefineRunId(null);
              }}
              placeholder="Example: 15s TikTok for a cold brew launch — energetic, Gen Z, neon accents, end with “Tap link in bio”. Tip: mention platform, audience, tone, key message, and CTA."
              rows={10}
              className="min-h-[200px] font-sans text-glass-text bg-glass-1 border-glass"
            />
            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => void onRefinePrompt()}
                disabled={!canRefine}
                className="border-glass border-accent2/40 text-glass-text hover:bg-glass-2"
                title="Charges 1 credit and returns three structured briefs you can pick from"
              >
                <Wand2 className="w-4 h-4 mr-2 inline-block text-accent2" />
                {refining ? 'Refining…' : `Refine prompt (${refineCreditCost} credit)`}
              </Button>
              <span className="text-xs text-glass-text-3 max-w-md">
                Turns your draft into three ready-to-generate briefs (scene beats, colors, pacing). Pick one to load
                into the box, then generate.
              </span>
            </div>

            {refineSummary && (
              <p className="text-sm text-glass-text-2 rounded-lg border border-glass bg-glass-2/50 px-3 py-2">
                {refineSummary}
              </p>
            )}

            {refineVariants && refineVariants.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-medium uppercase tracking-wide text-glass-text-3">Pick a direction</p>
                <div className="grid gap-2 sm:grid-cols-3">
                  {refineVariants.map((v) => {
                    const selected = selectedRefineId === v.id;
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() => applyRefineVariant(v)}
                        className={`text-left rounded-xl border px-3 py-3 transition-all ${
                          selected
                            ? 'border-accent2 bg-accent2/10 ring-1 ring-accent2/40'
                            : 'border-glass bg-glass-1 hover:-translate-y-0.5 hover:bg-glass-2 hover:border-glass-text-3/30 transition-all duration-150'
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span className="text-sm font-semibold text-glass-text">{v.label}</span>
                          {selected ? (
                            <Check className="w-4 h-4 shrink-0 text-accent2" aria-hidden />
                          ) : null}
                        </div>
                        <p className="mt-2 text-xs text-glass-text-3 line-clamp-4">{v.prompt}</p>
                        <span className="mt-2 inline-block text-xs text-accent2">Use this brief</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3 pt-1">
              <Button
                type="button"
                onClick={() => void onGenerate()}
                disabled={!canGenerate}
              >
                {generating ? 'Generating…' : `Generate (${generateCreditCost} credits)`}
              </Button>
              <span className="text-xs text-glass-text-3 self-center">
                Retries with the same run id are free; each new click uses a new id and charges again.
              </span>
            </div>
            {lastRunId && (
              <p className="text-xs text-glass-text-3 font-mono break-all">Last run: {lastRunId}</p>
            )}
            {refineRunId && (
              <p className="text-xs text-glass-text-3 font-mono break-all">Last refine: {refineRunId}</p>
            )}
          </CardContent>
        </Card>

        <Card className="p-5">
          <CardHeader>
            <CardTitle className="text-glass-text">Preview</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative mx-auto max-w-[320px] overflow-hidden rounded-xl border border-glass bg-black shadow-lg aspect-[9/16]">
              <Player
                component={PromptStudioComposition}
                inputProps={composition}
                durationInFrames={Math.max(1, durationInFrames)}
                compositionWidth={1080}
                compositionHeight={1920}
                fps={fps}
                controls
                style={{ width: '100%', height: '100%' }}
                clickToPlay
              />
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => void onExportMp4()}
                disabled={exporting || !hasGeneratedComposition}
                className="border-glass"
              >
                <Download className="w-4 h-4 mr-2 inline-block" />
                {exporting ? 'Exporting…' : `Download MP4 (${exportCreditCost} credits)`}
              </Button>
            </div>
            <p className="text-xs text-glass-text-3 mt-3">
              Vertical 9:16 MP4 matches the preview. Export runs on the server and needs a Chromium-capable environment
              (for example install Chrome or Chromium where the API runs).
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
